"use client";

import { cn } from "@/lib/utils";
import useTranslate from "@app/_hooks/useTranslate";
import Link from "@components/Link";
import WishlistPDPToggle from "@features/account/components/wishlist/WishlistPDPToggle";
import useAddToCart from "@features/cart/hooks/useAddToCart";
import { Button } from "@ui/button";
import { CheckIcon } from "lucide-react";
import { ComponentProps, type FormEvent } from "react";

import { useProductForm } from "../hooks/useProductForm";
import {
  lightsourceCartForParam,
  useLightsourceParam,
} from "../utils/lightsourceSelection";
import type { CartProduct, PurchaseProduct } from "../utils/toCartProduct";
import ProductQuantity from "./ProductQuantity";

const emptyLightsourceCarts: readonly CartProduct[] = [];

type ProductFormProps = {
  purchase: PurchaseProduct;
  countryCode: string;
  siteLocale: string;
  lightsourceCarts?: readonly CartProduct[];
  lightsourceParam?: string | null;
  className?: string;
} & ComponentProps<"div">;

export default function ProductForm({
  purchase,
  countryCode,
  siteLocale,
  lightsourceCarts = emptyLightsourceCarts,
  lightsourceParam = null,
  className,
  children,
}: ProductFormProps) {
  const t = useTranslate();
  const { mutateAsync: addToCart, isPending, isError } = useAddToCart();
  const {
    variantKey,
    recentlyAdded,
    setRecentlyAdded,
    quantity,
    decrease,
    increase,
    setQuantity,
    maxQuantity,
    atMax,
    disabled,
    oversellable,
  } = useProductForm(purchase);
  const selectedLightsource = useLightsourceParam(lightsourceParam);

  const added = recentlyAdded || isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!variantKey) return;

    const lightsource = lightsourceCartForParam(
      lightsourceCarts,
      selectedLightsource,
    );

    try {
      const items: CartProduct[] = [{ ...purchase, quantity }];
      if (lightsource)
        items.push({
          ...lightsource,
          quantity: purchase.lightsourceQuantity * quantity,
        });
      await addToCart(items);
      setRecentlyAdded(true);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className={cn("flex flex-col gap-y-2", className)}>
      <form
        id="product-cart-form"
        onSubmit={handleSubmit}
        className={cn("@container/form grid grid-cols-[auto_1fr_auto] gap-2")}
      >
        <input type="hidden" name="productKey" value={purchase.key} />
        <input type="hidden" name="market" value={countryCode} />
        <ProductQuantity
          value={quantity}
          onDecrease={decrease}
          onIncrease={increase}
          onChange={setQuantity}
          max={oversellable ? undefined : maxQuantity}
          disableIncrease={atMax}
          className="col-start-1"
        />
        <Button
          type="submit"
          disabled={disabled}
          data-testid="PDP-button-Add-to-Cart"
          className={cn(
            added && "pointer-events-none bg-green-600",
            "h-10 @max-sm/form:col-span-3 @max-sm/form:row-start-2",
          )}
        >
          {added ? (
            <CheckIcon strokeWidth={1} className="size-5" />
          ) : (
            t("addToCart")
          )}
        </Button>
        <WishlistPDPToggle
          productId={purchase.key}
          variantId={variantKey ?? ""}
          className="col-start-3 size-10"
        />
        {isError ? (
          <p className="text-error col-span-3 mt-1 text-sm">
            {t("errorAddToCart")}
          </p>
        ) : null}
        {recentlyAdded ? (
          <Button asChild className="col-span-full">
            <Link locale={siteLocale} href="/checkout">
              {t("goToCheckout")}
            </Link>
          </Button>
        ) : null}
      </form>
      {children}
    </div>
  );
}
